import { useState, useEffect } from 'react';
import {
  listOrgs,
  readOrgFile,
  writeOrgFile,
  orgExists,
} from '../storage/FileSystemAdapter';
import { maybeAdvancePdcaStage } from '../utils/pdcaStage';
import { SUPPORTED_NORMS } from '../utils/enums';
import Tooltip from './shared/Tooltip';
import { BTN_PRIMARY, BTN_SECONDARY, TEXT_MUTED, BORDER_DEFAULT, TEXT_HEADING, BG_SURFACE, BRAND_PRIMARY, BG_SHELL, FIELD_ERROR_BORDER, ERROR_TEXT, SURFACE_WHITE, FONT_BODY, FONT_BODY_STRONG, FONT_SMALL, FONT_PAGE_TITLE, FONT_SECTION_TITLE, FONT_BUTTON, FONT_LABEL } from '../theme';
import { COUNTRIES } from '../utils/countries';
import { SECTORS } from '../utils/sectors';

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:           'Mis organizaciones',
    empty:           'No hay organizaciones todavía. Crea una nueva para empezar.',
    loading:         'Cargando…',
    newOrg:          '+ Nueva organización',
    formTitle:       'Nueva organización',
    labelName:       'Nombre',
    labelSector:     'Sector',
    labelCountry:    'País',
    labelLanguage:   'Idioma por defecto de la organización',
    orgLanguageTooltip: 'Usado en el Executive Dashboard y como contexto en prompts de IA. El selector de idioma del encabezado controla el idioma de la interfaz.',
    langEs:          'Español',
    langEn:          'Inglés',
    placeholderName:    'Ej. Acme Corp',
    placeholderSector:  'Ej. Tecnología, Salud, Finanzas',
    placeholderCountry: 'Ej. España',
    required:        'Campo obligatorio',
    btnCreate:       'Crear organización',
    btnCancel:       'Cancelar',
    creating:        'Creando…',
    errorCreate:     'Error al crear la organización. Inténtalo de nuevo.',
  },
  en: {
    title:           'My organizations',
    empty:           'No organizations yet. Create one to get started.',
    loading:         'Loading…',
    newOrg:          '+ New organization',
    formTitle:       'New organization',
    labelName:       'Name',
    labelSector:     'Sector',
    labelCountry:    'Country',
    labelLanguage:   'Default organization language',
    orgLanguageTooltip: 'Used in the Executive Dashboard and as context in AI prompts. The language selector in the header controls the interface language.',
    langEs:          'Spanish',
    langEn:          'English',
    placeholderName:    'e.g. Acme Corp',
    placeholderSector:  'e.g. Technology, Healthcare, Finance',
    placeholderCountry: 'e.g. Spain',
    required:        'Required field',
    btnCreate:       'Create organization',
    btnCancel:       'Cancel',
    creating:        'Creating…',
    errorCreate:     'Failed to create the organization. Please try again.',
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function toSlug(name) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const EMPTY_FORM = { name: '', sector: '', country: [], language: 'en' };

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function OrgSelector({ lang, onSelect }) {
  const [orgs, setOrgs] = useState(null);       // null = loading
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const t = T[lang] || T.en;

  // ── Load orgs on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ids = await listOrgs();
        const settled = await Promise.all(
          ids.map((id) =>
            readOrgFile(id, 'profile.json')
              .then((p) => {
                if (!p) return p;
                // Backwards-compatible migration: country legacy values were
                // string | null; the field is now string[].
                const c = p.country;
                let migrated;
                if (Array.isArray(c)) migrated = c;
                else if (typeof c === 'string' && c !== '') migrated = [c];
                else migrated = [];
                return { ...p, country: migrated };
              })
              .catch(() => null)
          )
        );
        if (!cancelled) setOrgs(settled.filter(Boolean));
      } catch {
        if (!cancelled) setOrgs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddCountry = (code) => {
    if (!code) return;
    setForm((prev) =>
      prev.country.includes(code)
        ? prev
        : { ...prev, country: [...prev.country, code] }
    );
    setErrors((prev) => ({ ...prev, country: undefined }));
  };

  const handleRemoveCountry = (code) =>
    setForm((prev) => ({
      ...prev,
      country: prev.country.filter((c) => c !== code),
    }));

  const openForm = () => {
    setForm({ ...EMPTY_FORM, language: lang });
    setErrors({});
    setCreateError(null);
    setShowForm(true);
  };

  const closeForm = () => setShowForm(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim())    errs.name    = t.required;
    if (!form.sector.trim())  errs.sector  = t.required;
    if (form.country.length === 0) errs.country = t.required;
    return errs;
  };

  // ── Create org ──────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setCreating(true);
    setCreateError(null);
    try {
      let base = toSlug(form.name.trim()) || 'org';
      let slug = base;
      let n = 1;
      while (await orgExists(slug)) { slug = `${base}-${n++}`; }

      const now = new Date().toISOString();
      const profile = {
        id: slug,
        name: form.name.trim(),
        logo: null,
        sector: form.sector.trim(),
        size: 0,
        country: form.country,
        language: form.language,
        certificationStatus: 'none',
        certificationBody: null,
        certificationDate: null,
        recertificationDate: null,
        certificateNumber: null,
        stage1AuditDate: null,
        stage2AuditDate: null,
        pdcaStage: 'Plan',
        auditFrequencyMonths: 12,
        mrFrequencyMonths: 12,
        riskAcceptanceThreshold: 'LOW',
        riskMatrix: { thresholds: { low: 4, medium: 9 } },
        ismsRoles: {
          topManagementSignatory: [],
          ismsManager: null,
          ciso: null,
          dpo: null,
          nis2Contact: null,
        },
        refPrefixes: {
          asset: 'ast',
          audit: 'aud',
          customer: 'cus',
          document: 'doc',
          evidence: 'evd',
          incident: 'inc',
          legalRegister: 'lrg',
          managementReview: 'mrv',
          metric: 'met',
          nonconformity: 'nco',
          objective: 'obj',
          risk: 'rsk',
          supplier: 'sup',
        },
        topManagementCommitmentEvidenceId: null,
        documentClassification: ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'],
        defaultClassification: 'INTERNAL',
        documentReviewPeriodMonths: 12,
        riskApproach: 'event-based',
        technologies: [],
        aiContext: '',
        norms: [SUPPORTED_NORMS[0]],
        aiProcessingConsent: true,
        aiProcessingConsentDate: now,
        alertSnoozes: {},
        createdAt: now,
        updatedAt: now,
      };

      await writeOrgFile(slug, 'profile.json', profile);
      localStorage.setItem('grc-active-org', slug);
      onSelect(slug, profile);
    } catch {
      setCreateError(t.errorCreate);
    } finally {
      setCreating(false);
    }
  };

  // ── Select existing org ──────────────────────────────────────────────────────
  const handleSelectOrg = async (org) => {
    localStorage.setItem('grc-active-org', org.id);
    // §4 Organization: evaluate pdcaStage auto-advance on org load so the
    // picked org reflects the correct PDCA phase before the dashboard renders.
    // Best-effort — failures leave the stage unchanged.
    const advanced = await maybeAdvancePdcaStage(org.id, org).catch(() => org);
    onSelect(org.id, advanced || org);
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const inputStyle = (hasError) => ({
    ...FONT_BODY,
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: `1px solid ${hasError ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
    borderRadius: 6,
    background: SURFACE_WHITE,
    color: TEXT_HEADING,
    outline: 'none',
    boxSizing: 'border-box',
  });

  const fieldBlock = { marginBottom: '1.1rem' };

  const labelStyle = {
    ...FONT_SMALL,
    display: 'block',
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  };

  const errorMsg = {
    ...FONT_SMALL,
    color: ERROR_TEXT,
    marginTop: '0.25rem',
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: 'calc(100vh - 52px)',
      background: BG_SHELL,
      padding: '2.5rem 1.5rem',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.75rem',
        }}>
          <h1 style={{
            ...FONT_PAGE_TITLE,
            fontWeight: 700,
            color: TEXT_HEADING,
            margin: 0,
          }}>
            {t.title}
          </h1>
          {!showForm && (
            <button
              onClick={openForm}
              style={{
                ...BTN_PRIMARY,
                ...FONT_BUTTON,
                border: 'none',
                borderRadius: 8,
                padding: '0.55rem 1.25rem',
                cursor: 'pointer',
              }}
            >
              {t.newOrg}
            </button>
          )}
        </div>

        {/* Org list */}
        {orgs === null && (
          <p style={{
            ...FONT_LABEL,
            color: TEXT_MUTED,
          }}>
            {t.loading}
          </p>
        )}

        {orgs !== null && orgs.length === 0 && !showForm && (
          <p style={{
            ...FONT_BODY,
            color: TEXT_MUTED,
            textAlign: 'center',
            marginTop: '3rem',
          }}>
            {t.empty}
          </p>
        )}

        {orgs !== null && orgs.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '1rem',
            marginBottom: showForm ? '2rem' : 0,
          }}>
            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrg(org)}
                style={{
                  background: BG_SURFACE,
                  border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 10,
                  padding: '1.25rem 1.5rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = BRAND_PRIMARY;
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(26,92,58,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = BORDER_DEFAULT;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  ...FONT_BODY_STRONG,
                  color: TEXT_HEADING,
                  marginBottom: '0.35rem',
                }}>
                  {org.name}
                </div>
                <div style={{
                  ...FONT_LABEL,
                  color: TEXT_MUTED,
                  letterSpacing: '0.03em',
                }}>
                  {(SECTORS.find(s => s.value === org.sector)?.[lang === 'es' ? 'nameEs' : 'nameEn']) || org.sector}
                  {(() => {
                    const arr = Array.isArray(org.country)
                      ? org.country
                      : (typeof org.country === 'string' && org.country !== '' ? [org.country] : []);
                    if (arr.length === 0) return '';
                    const display = arr
                      .map((code) => (COUNTRIES.find(c => c.code === code)?.[lang === 'es' ? 'nameEs' : 'nameEn']) || code)
                      .join(' · ');
                    return ` · ${display}`;
                  })()}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Inline create form */}
        {showForm && (
          <div style={{
            background: BG_SURFACE,
            border: `1px solid ${BORDER_DEFAULT}`,
            borderRadius: 12,
            padding: '2rem',
            maxWidth: 480,
          }}>
            <h2 style={{
              ...FONT_SECTION_TITLE,
              color: TEXT_HEADING,
              margin: '0 0 1.5rem',
            }}>
              {t.formTitle}
            </h2>

            {/* Name */}
            <div style={fieldBlock}>
              <label style={labelStyle}>{t.labelName}</label>
              <input
                type="text"
                value={form.name}
                onChange={setField('name')}
                placeholder={t.placeholderName}
                style={inputStyle(!!errors.name)}
                disabled={creating}
              />
              {errors.name && <p style={errorMsg}>{errors.name}</p>}
            </div>

            {/* Sector */}
            <div style={fieldBlock}>
              <label style={labelStyle}>{t.labelSector}</label>
              <select
                value={form.sector}
                onChange={setField('sector')}
                style={inputStyle(!!errors.sector)}
                disabled={creating}
              >
                <option value="">—</option>
                {SECTORS.map(s => (
                  <option key={s.value} value={s.value}>
                    {lang === 'es' ? s.nameEs : s.nameEn}
                  </option>
                ))}
              </select>
              {errors.sector && <p style={errorMsg}>{errors.sector}</p>}
            </div>

            {/* Country */}
            <div style={fieldBlock}>
              <label style={labelStyle}>{t.labelCountry}</label>
              {form.country.length > 0 && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
                  marginBottom: '0.5rem',
                }}>
                  {form.country.map((code) => {
                    const c = COUNTRIES.find((x) => x.code === code);
                    const label = c
                      ? (lang === 'es' ? c.nameEs : c.nameEn)
                      : code;
                    return (
                      <span key={code} style={{
                        ...FONT_SMALL,
                        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                        background: BG_SHELL, border: `1px solid ${BORDER_DEFAULT}`,
                        borderRadius: 999, padding: '0.2rem 0.6rem',
                        color: TEXT_HEADING,
                      }}>
                        {label}
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(code)}
                          disabled={creating}
                          aria-label={label}
                          style={{
                            background: 'transparent', border: 'none',
                            color: TEXT_MUTED,
                            cursor: creating ? 'not-allowed' : 'pointer',
                            padding: 0, lineHeight: 1, fontSize: '1rem',
                          }}
                        >×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <select
                value=""
                onChange={(e) => {
                  handleAddCountry(e.target.value);
                  e.target.value = '';
                }}
                style={inputStyle(!!errors.country)}
                disabled={creating}
              >
                <option value="" disabled>—</option>
                {COUNTRIES.filter((c) => !form.country.includes(c.code)).map((c) => (
                  <option key={c.code} value={c.code}>
                    {lang === 'es' ? c.nameEs : c.nameEn}
                  </option>
                ))}
              </select>
              {errors.country && <p style={errorMsg}>{errors.country}</p>}
            </div>

            {/* Language */}
            <div style={{ ...fieldBlock, marginBottom: '1.75rem' }}>
              <Tooltip text={t.orgLanguageTooltip}>
                <label style={labelStyle}>{t.labelLanguage}</label>
              </Tooltip>
              <div style={{
                display: 'flex',
                border: `1px solid ${BORDER_DEFAULT}`,
                borderRadius: 6,
                overflow: 'hidden',
                width: 'fit-content',
              }}>
                {[{ code: 'es', label: t.langEs }, { code: 'en', label: t.langEn }].map(({ code, label }) => {
                  const active = form.language === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, language: code }))}
                      disabled={creating}
                      style={{
                        ...FONT_LABEL,
                        background: active ? TEXT_HEADING : 'transparent',
                        color: active ? SURFACE_WHITE : TEXT_MUTED,
                        border: 'none',
                        padding: '0.4rem 1rem',
                        cursor: creating ? 'not-allowed' : 'pointer',
                        letterSpacing: '0.04em',
                        transition: 'all 0.15s',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Create error */}
            {createError && (
              <p style={{
                ...errorMsg,
                marginBottom: '1rem',
                fontSize: '0.82rem',
              }}>
                {createError}
              </p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{
                  ...BTN_PRIMARY,
                  ...FONT_BUTTON,
                  background: creating ? '#4b7c61' : BTN_PRIMARY.background,
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.65rem 1.5rem',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {creating ? t.creating : t.btnCreate}
              </button>
              <button
                onClick={closeForm}
                disabled={creating}
                style={{
                  ...BTN_SECONDARY,
                  ...FONT_BUTTON,
                  borderRadius: 8,
                  padding: '0.65rem 1.25rem',
                  cursor: creating ? 'not-allowed' : 'pointer',
                }}
              >
                {t.btnCancel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
